import { HardRequirementQuestionKey } from '../model';
import { QuestionKey } from '../model';
import type { HardRequirementQuestionDTO, SupersededQuestionKeys } from '../model';

/**
 * Hard questions that ask what a normal question already asks.
 *
 * The two key spaces are disjoint, so the overlap cannot be detected by comparing keys: the pairs
 * below are semantic, and singular-against-plural is a coincidence of naming rather than a rule to
 * derive from. Everything not listed here — `education`, `driver_license`, `physical_demand` and
 * the rest — has no normal counterpart and never hides anything.
 *
 * Note that the two sides do not agree on spelling either. A `certificate` question asks for
 * `['heftruck', 'ept']` where `certificates_and_licenses` carries `['Heftruckcertificaat', 'EPT']`,
 * which is why this maps keys and not values.
 */
const supersededByHardQuestion: SupersededQuestionKeys = {
  [HardRequirementQuestionKey.CERTIFICATE]: QuestionKey.CERTIFICATES_AND_LICENSES,
  [HardRequirementQuestionKey.LANGUAGE]: QuestionKey.LANGUAGES,
  [HardRequirementQuestionKey.SHIFT]: QuestionKey.SHIFTS,
  [HardRequirementQuestionKey.DAILY_WORKING_TIME]: QuestionKey.WORKING_HOURS_PER_WEEK,
};

/**
 * The normal questions to leave unrendered, given the hard questions the candidate was served.
 *
 * The hard question wins because it is the gate the candidate is scored against; showing both means
 * asking the same thing twice on one page.
 */
/**
 * The normal question one hard question supersedes, if any.
 *
 * The per-question lookup behind `getSupersededQuestionKeys`, exposed because carrying a hard answer
 * across to the requirement it replaced needs the pair, not just the list of hidden keys.
 */
export const getSupersededQuestionKey = (hardKey: HardRequirementQuestionKey): QuestionKey | undefined =>
  supersededByHardQuestion[hardKey];

export const getSupersededQuestionKeys = (hardQuestions: readonly HardRequirementQuestionDTO[]): QuestionKey[] => [
  ...new Set(
    hardQuestions
      .map((question) => supersededByHardQuestion[question.question_key])
      .filter((key): key is QuestionKey => key !== undefined),
  ),
];
